import { TZDate } from '@date-fns/tz';
import { BUSINESS_TIME_ZONE, Monthly10k } from '@daymade/common-lib/constants';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useState } from 'react';
import { tryApi } from 'utils/api/common';
import { drawApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import CurvyOctagon from '../svg/CurvyOctagon.svg';
import styles from './ProgressionBar.module.css';
import {
  placeTicketsIntoWeekMarkers,
  type WeekMarker,
} from './ProgressionBar.utils';

interface Props {
  shortMonthName: string | undefined;
  weekMarkers: (WeekMarker | undefined)[];
  customProgressTitle?: string | ReactNode;
  size?: 'large' | 'normal';
}
const weeksToRender = Array.from({ length: 5 }, (_, i) => i);

const ProgressionBar = ({
  shortMonthName,
  weekMarkers,
  customProgressTitle,
  size = 'normal',
}: Props) => {
  const total = weekMarkers.reduce((acc, x) => acc + (x?.earnings || 0), 0);

  return (
    <div
      className={
        styles.progressionBar +
        ' aboutUsTypographyCaption' +
        ` ${styles['progressionBar__' + size]}`
      }
    >
      <div className={styles.sideBySide}>
        {customProgressTitle ? (
          customProgressTitle
        ) : (
          <p className={styles.introText + ' aboutUsTypographyCaption'}>
            Automatically earn{' '}
            <strong>{Monthly10k.minimumEntriesPerWeek} entries</strong> every
            week
          </p>
        )}
        <ol className={styles.barContainer}>
          {weeksToRender.map((x) => {
            return (
              <li
                key={x}
                className={`${styles.week} ${weekMarkers[x]?.earnings ? styles.highlighted : ''} ${weekMarkers[x]?.isMidweek ? styles.midWeek : ''}`}
              >
                <div className={styles.weekLabel}>
                  {shortMonthName || '\u00A0'} {weekMarkers[x]?.dayNumber}
                </div>
                <div className={`${styles.shapeContainer}`}>
                  <CurvyOctagon className={styles.shape} />
                  {weekMarkers[x]?.earnings ? (
                    <span className={styles.pointsEarnt}>
                      +{weekMarkers[x].earnings}{' '}
                      <span className="visuallyHidden">points earnt</span>
                    </span>
                  ) : (
                    <span className={styles.pointsEarnt}>
                      {shortMonthName
                        ? '+' + Monthly10k.minimumEntriesPerWeek
                        : '\u00A0'}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
      <div className={styles.summaryText}>
        Total
        <strong className={styles.summaryTextAlt}>
          {shortMonthName ? total : '\u00A0'}
        </strong>
        <span className={styles.summaryTextAlt}>entries</span>
      </div>
    </div>
  );
};

export const CurrentUserProgressionBar = observer(
  ({
    customProgressTitle,
    size = 'normal',
  }: {
    customProgressTitle?: string | ReactNode;
    size?: 'large' | 'normal';
  }) => {
    const userStore = useUserStore();

    // Find the tickets we're interested in
    const bigRaffleTickets = userStore.activeTickets.filter(
      (x) => x.drawType.code == Monthly10k.drawTypeCode,
    );

    // Render the progression bar as the draw date for the raffle tickets that we're interested in, if we have some
    const [drawDate, setDrawDate] = useState<Date | undefined>(
      bigRaffleTickets[0]?.draw?.drawDate || undefined,
    );

    // Otherwise, make a request to figure out the current monthly 10k giveaway draw date
    useEffect(() => {
      if (!drawDate) {
        (async () => {
          const draw = await tryApi(
            drawApiClient.public.getDrawTypeCached({
              params: { draw_code: Monthly10k.drawTypeCode },
            }),
          );
          if (draw.status === HttpStatusCode.OK) {
            setDrawDate(draw.body.next_draw?.drawDate || undefined);
          }
        })();
      }
    }, []);

    // If we don't know the draw date, then render the progression bar without much detail
    if (!drawDate) {
      return (
        <ProgressionBar
          shortMonthName={undefined}
          weekMarkers={[]}
          customProgressTitle={customProgressTitle}
          size={size}
        />
      );
    }
    const startOfMonth = new TZDate(
      drawDate.getFullYear(),
      drawDate.getMonth(),
      1,
      0,
      0,
      0,
      0,
      BUSINESS_TIME_ZONE,
    );

    const shortMonthName = new Intl.DateTimeFormat('en-GB', {
      month: 'short',
      timeZone: BUSINESS_TIME_ZONE,
    }).format(startOfMonth);

    const days =
      /* Note that February is 1 rather than zero, as months are zero-indexed */
      startOfMonth.getMonth() === 1
        ? Monthly10k.entryAwardDayOfMonthFeb
        : Monthly10k.entryAwardDayOfMonthOther;

    const now = new Date();
    const weekMarkers: WeekMarker[] = days.map((x) => ({
      dayNumber: x,
      earnings: 0,
      isMidweek: now.getDate() != x,
    }));

    placeTicketsIntoWeekMarkers(startOfMonth, bigRaffleTickets, weekMarkers);

    return (
      <ProgressionBar
        shortMonthName={shortMonthName}
        weekMarkers={weekMarkers}
        customProgressTitle={customProgressTitle}
        size={size}
      />
    );
  },
);

export default ProgressionBar;
